// ============================================================
// Impersonation — Netcom-admin "View as customer" mode
// ============================================================
// Stored in sessionStorage so it auto-clears on tab close (defense in
// depth — even if a Netcom staff member walks away, the impersonation
// dies with the browser tab). The actual auth is the Cognito JWT,
// which carries `cognito:groups: ["netcom-admin"]`. The backend
// honors X-Assume-Account ONLY when claims.isNetcomAdmin === true.
// Local mutation alone is not sufficient to scope another customer's
// data — the server is the source of truth.

import { DEMO_PRODUCTS, DEMO_ACCOUNT_NUMBER } from '../config/demo.js';

const KEY = 'netcom-impersonation';

/**
 * Demo/sample accounts always showcase the full product set.
 *
 * Every demo persona is meant to demonstrate BOTH Starlink and the Netcom
 * Connect SIM (the multi-carrier KORE eSIM) — that is the point of the demo.
 * But `products` reaches startImpersonation from six different call sites,
 * and the ones that read it off a Cognito record (AdminAccounts, TopBar,
 * AdminCustomer) get an empty array, because staff and demo accounts do not
 * carry a custom:products claim.
 *
 * An empty set does not degrade gracefully — Sidebar gates whole sections on
 * it, so the Fleet section (Network Map, Dishes, Service Lines) AND the
 * Netcom Connect SIM section both disappear, and the demo looks like a
 * product with no features. That regressed twice before (2026-08-13, and
 * again when the SIM section was added).
 *
 * Scoped deliberately to demo accounts. A REAL customer with no products
 * keeps an empty set — inventing entitlements for a paying customer would
 * show them a product they do not have.
 */
function isDemoAccount(accountNumber) {
  const a = String(accountNumber || '').toUpperCase();
  return a === DEMO_ACCOUNT_NUMBER || a.startsWith('ACC-DEMO-') || a.startsWith('ACC-CTO-');
}

export function getImpersonation() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    // Auto-expire after the server-side TTL (1 hour by default).
    if (obj.exp && obj.exp * 1000 < Date.now()) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return obj;
  } catch {
    return null;
  }
}

export function startImpersonation({ accountNumber, businessName, email, exp, actorEmail, products }) {
  if (!accountNumber) throw new Error('accountNumber required');
  const obj = {
    accountNumber,
    businessName: businessName || accountNumber,
    email: email || null,
    exp: exp || Math.floor(Date.now() / 1000) + 60 * 60,
    actorEmail: actorEmail || null,
    startedAt: Math.floor(Date.now() / 1000),
    // BUG THIS FIXES (2026-08-13): without this, Sidebar.jsx gated the
    // Starlink/SIM nav sections on the IMPERSONATING STAFF MEMBER's own
    // custom:products claim (almost always empty — staff accounts don't
    // carry it) instead of the customer being viewed. Every "View as
    // customer" session lost the entire Starlink section from the
    // sidebar — Network Map, Dishes, Service Lines all vanished. Found
    // live testing "view as Radius Security" right after shipping nav
    // badges for those exact items.
    products: (() => {
      const given = Array.isArray(products) ? products.filter(Boolean) : [];
      if (given.length > 0) return given;
      // Only demo accounts get a default — see isDemoAccount above.
      return isDemoAccount(accountNumber) ? [...DEMO_PRODUCTS] : [];
    })(),
  };
  sessionStorage.setItem(KEY, JSON.stringify(obj));
  // Notify the rest of the app so banners + sidebars re-render.
  window.dispatchEvent(new CustomEvent('netcom:impersonation-changed'));
  return obj;
}

export function exitImpersonation() {
  sessionStorage.removeItem(KEY);
  window.dispatchEvent(new CustomEvent('netcom:impersonation-changed'));
}

/**
 * Inject the X-Assume-Account header on every fetch when impersonating.
 * Call once at app boot.
 */
export function installImpersonationFetchInterceptor() {
  if (typeof window === 'undefined' || window.__netcomImpFetchInstalled) return;
  window.__netcomImpFetchInstalled = true;

  const origFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const imp = getImpersonation();
    if (!imp) return origFetch(input, init);

    // Build a new init with the X-Assume-Account header. Don't mutate
    // the caller's options object.
    const newHeaders = new Headers(init.headers || {});
    newHeaders.set('X-Assume-Account', imp.accountNumber);
    return origFetch(input, { ...init, headers: newHeaders });
  };
}
